import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Users, UserPlus, Send, Loader2, Eye, AlertTriangle, Check, Clock, Copy, ChevronRight, MessageSquare, X, CreditCard, FileDown } from "lucide-react";
import { toast } from "sonner";
import { SpurwechselLogo } from "@/components/SpurwechselLogo";

const STATUS_CONFIG = {
  bereit: { color: "bg-success", badge: "default", label: "Bereit" },
  simulation_bestanden: { color: "bg-success", badge: "default", label: "Simulation bestanden" },
  bearbeitung: { color: "bg-warning", badge: "secondary", label: "In Bearbeitung" },
  grundlagen: { color: "bg-orange-400", badge: "secondary", label: "Grundlagen" },
  inaktiv: { color: "bg-muted-foreground", badge: "outline", label: "Inaktiv" },
  neu: { color: "bg-destructive", badge: "outline", label: "Neu" },
};

const COMP_LABELS = {
  vorfall_darstellung: "Vorfalldarstellung", eigenverantwortung: "Eigenverantwortung",
  selbstreflexion: "Selbstreflexion", veraenderungsnachweis: "Veränderungsnachweis",
  rueckfallpraevention: "Rückfallprävention", gespraechskompetenz: "Gesprächskompetenz",
};

const STAGE_NAMES = {
  ankommen: "Ankommen", verstehen: "Verstehen", aufarbeiten: "Aufarbeiten",
  trainieren: "Trainieren", vertiefen: "Vertiefen", simulation: "Prüfungssimulation"
};

function BillingSection({ billing, loading, onRefresh }) {
  const [canceling, setCanceling] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [checkingOut, setCheckingOut] = useState(null);

  if (loading || !billing) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const b = billing.billing || {};
  const status = billing.status;
  const planName = billing.plan_name || "—";

  const handleCancel = async () => {
    if (!confirm("Möchten Sie Ihren Plan wirklich kündigen? Der Zugang bleibt bis zum Ende des Zeitraums bestehen.")) return;
    setCanceling(true);
    try {
      await api.post("/b2b/cancel");
      toast.success("Plan gekündigt");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Plan konnte nicht gekündigt werden"); }
    setCanceling(false);
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      await api.post("/b2b/reactivate");
      toast.success("Plan reaktiviert");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Plan konnte nicht reaktiviert werden"); }
    setReactivating(false);
  };

  const handleCheckout = async (planKey) => {
    setCheckingOut(planKey);
    try {
      const res = await api.post("/b2b/checkout", { plan: planKey, origin_url: window.location.origin });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) { toast.error(err.response?.data?.detail || "Checkout fehlgeschlagen"); }
    setCheckingOut(null);
  };

  // No subscription yet
  if (!billing.has_subscription) {
    return (
      <Card className="border-border/50" data-testid="billing-no-plan">
        <CardContent className="pt-6 text-center">
          <CreditCard className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <h3 className="font-heading text-lg font-bold mb-1">Kein aktiver Plan</h3>
          <p className="font-body text-sm text-muted-foreground mb-4">Wählen Sie einen Plan, um alle Funktionen zu nutzen.</p>
          <PlanSelector plans={billing.plans} />
        </CardContent>
      </Card>
    );
  }

  // Trial expired
  if (status === "expired" || (status === "trial" && billing.trial_days_left <= 0)) {
    return (
      <Card className="border-warning/30 bg-warning/5" data-testid="billing-expired">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-warning mt-0.5" />
            <div>
              <h3 className="font-heading text-lg font-bold">Testphase beendet</h3>
              <p className="font-body text-sm text-muted-foreground mt-1">Ihr Testzeitraum ist abgelaufen. Ihre Teilnehmer und Daten bleiben gespeichert.</p>
            </div>
          </div>
          <PlanSelector plans={billing.plans} mode="checkout" onCheckout={handleCheckout} checkingOut={checkingOut} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/50" data-testid="billing-section">
      <CardContent className="pt-6 space-y-5">
        {/* Plan-Status */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-heading text-lg font-bold">{planName}</h3>
              <Badge variant={status === "trial" ? "secondary" : status === "canceled" ? "outline" : "default"} className="font-body text-xs">
                {status === "trial" ? "Testphase" : status === "active" ? "Aktiv" : status === "canceled" ? "Gekündigt" : status === "past_due" ? "Zahlung offen" : status}
              </Badge>
            </div>
            {status === "trial" && (
              <p className="font-body text-sm text-muted-foreground mt-1">Noch {billing.trial_days_left} Tage kostenlos</p>
            )}
            {status === "canceled" && billing.period_days_left > 0 && (
              <p className="font-body text-sm text-muted-foreground mt-1">Zugang noch {billing.period_days_left} Tage</p>
            )}
          </div>
        </div>

        {/* Teilnehmer */}
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="font-body text-xs font-medium text-muted-foreground mb-2">Teilnehmer</p>
          <div className="flex items-baseline gap-1">
            <span className="font-heading text-2xl font-bold">{billing.active_participants}</span>
            <span className="font-body text-sm text-muted-foreground">aktiv</span>
            <span className="font-body text-sm text-muted-foreground mx-1">·</span>
            <span className="font-body text-sm text-muted-foreground">{b.included_participants} inklusive</span>
            {b.extra_participants > 0 && (
              <span className="font-body text-sm text-warning ml-1">+{b.extra_participants} extra</span>
            )}
          </div>
        </div>

        {/* Kosten (nur wenn nicht Trial) */}
        {status !== "trial" && b.total_netto > 0 && (
          <div>
            <p className="font-body text-xs font-medium text-muted-foreground mb-2">Monatskosten</p>
            <table className="w-full font-body text-sm">
              <tbody>
                <tr><td className="py-1 text-muted-foreground">Grundgebühr</td><td className="py-1 text-right font-medium">{b.base_fee?.toFixed(2)} €</td></tr>
                {b.extra_participants > 0 && (
                  <tr><td className="py-1 text-muted-foreground">{b.extra_participants} × {b.extra_participant_price?.toFixed(2)} € extra</td><td className="py-1 text-right font-medium">{b.extra_fee?.toFixed(2)} €</td></tr>
                )}
                <tr className="border-t border-border/50"><td className="pt-2 text-muted-foreground">Gesamt netto</td><td className="pt-2 text-right font-bold">{b.total_netto?.toFixed(2)} €</td></tr>
                <tr><td className="py-0.5 text-xs text-muted-foreground">zzgl. 19% MwSt</td><td className="py-0.5 text-right text-xs text-muted-foreground">{b.mwst?.toFixed(2)} €</td></tr>
                <tr><td className="py-0.5 text-xs text-muted-foreground">Gesamt brutto</td><td className="py-0.5 text-right text-xs text-muted-foreground">{b.total_brutto?.toFixed(2)} €</td></tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Trial: Kosten nach Testphase */}
        {status === "trial" && (
          <div className="bg-muted/20 rounded-lg p-3">
            <p className="font-body text-xs text-muted-foreground">Nach der Testphase: <strong>{b.base_fee?.toFixed(2)} €/Monat</strong> (zzgl. MwSt)</p>
            <p className="font-body text-xs text-muted-foreground mt-0.5">Zusätzliche Teilnehmer: {b.extra_participant_price?.toFixed(2)} €/TN/Monat</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          {status === "trial" && (
            <Button size="sm" className="font-body text-xs bg-primary text-primary-foreground" onClick={() => handleCheckout(billing.plan)} disabled={!!checkingOut} data-testid="checkout-btn">
              {checkingOut ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CreditCard className="w-3 h-3 mr-1" />}
              Jetzt Plan aktivieren
            </Button>
          )}
          {(status === "trial" || status === "active") && (
            <Button variant="outline" size="sm" className="font-body text-xs" onClick={handleCancel} disabled={canceling} data-testid="cancel-plan-btn">
              {canceling ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              Plan kündigen
            </Button>
          )}
          {status === "canceled" && billing.period_days_left > 0 && (
            <Button size="sm" className="font-body text-xs bg-primary text-primary-foreground" onClick={handleReactivate} disabled={reactivating} data-testid="reactivate-plan-btn">
              {reactivating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              Kündigung rückgängig
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function PlanSelector({ plans, mode = "trial", onCheckout, checkingOut }) {
  const [subscribing, setSubscribing] = useState(null);

  const subscribe = async (planKey) => {
    setSubscribing(planKey);
    try {
      const res = await api.post("/b2b/subscribe", { plan: planKey });
      toast.success(res.data.message);
      window.location.reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Abonnement konnte nicht gestartet werden"); }
    setSubscribing(null);
  };

  const handleClick = (planKey) => {
    if (mode === "checkout" && onCheckout) {
      onCheckout(planKey);
    } else {
      subscribe(planKey);
    }
  };

  if (!plans) return null;

  const isLoading = (key) => mode === "checkout" ? checkingOut === key : subscribing === key;

  return (
    <div className="grid sm:grid-cols-2 gap-3 mt-4" data-testid="plan-selector">
      {Object.entries(plans).map(([key, plan]) => (
        <Card key={key} className={`border-border/50 ${key === "b2b_professional" ? "ring-2 ring-primary/30" : ""}`}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-heading text-base font-bold">{plan.name}</h4>
              {key === "b2b_professional" && <Badge className="font-body text-[10px]">Empfohlen</Badge>}
            </div>
            <p className="font-heading text-2xl font-bold">{plan.price?.toFixed(0)} €<span className="font-body text-sm font-normal text-muted-foreground">/Monat</span></p>
            <p className="font-body text-xs text-muted-foreground mt-1">{plan.included_participants} Teilnehmer inklusive · +{plan.extra_participant_price?.toFixed(0)} €/extra TN</p>
            <p className="font-body text-xs text-muted-foreground">Alle Preise zzgl. MwSt</p>
            <Button className="w-full mt-3 font-body text-sm bg-primary text-primary-foreground" size="sm" onClick={() => handleClick(key)} disabled={isLoading(key)} data-testid={`select-plan-${key}`}>
              {isLoading(key) ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              {mode === "checkout" ? "Plan wählen und bezahlen" : "14 Tage kostenlos testen"}
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function ExpertDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isExpert, setIsExpert] = useState(false);
  const [view, setView] = useState("list"); // list | invite | detail | billing
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [clientAkte, setClientAkte] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviting, setInviting] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);
  const [markNote, setMarkNote] = useState("");
  const [markDate, setMarkDate] = useState("");
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  const fetchDashboard = async () => {
    try {
      const res = await api.get("/expert/dashboard");
      setDashboard(res.data);
      setIsExpert(true);
    } catch (e) {
      if (e.response?.status === 403) setIsExpert(false);
    }
    setLoading(false);
  };

  useEffect(() => { fetchDashboard(); }, []);

  // ─── Payment Success Polling ───
  useEffect(() => {
    const paymentStatus = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");
    if (paymentStatus === "success" && sessionId) {
      setView("billing");
      setPaymentProcessing(true);
      let attempts = 0;
      const poll = async () => {
        try {
          const res = await api.get(`/b2b/payment-status/${sessionId}`);
          if (res.data.payment_status === "paid") {
            toast.success(res.data.message || "Plan aktiviert!");
            setPaymentProcessing(false);
            searchParams.delete("payment");
            searchParams.delete("session_id");
            setSearchParams(searchParams, { replace: true });
            fetchBilling();
            return;
          }
          if (res.data.payment_status === "expired" || res.data.payment_status === "error") {
            toast.error("Zahlung konnte nicht verarbeitet werden.");
            setPaymentProcessing(false);
            searchParams.delete("payment");
            searchParams.delete("session_id");
            setSearchParams(searchParams, { replace: true });
            return;
          }
        } catch { /* ignore */ }
        attempts++;
        if (attempts < 10) setTimeout(poll, 2000);
        else {
          setPaymentProcessing(false);
          toast.error("Zeitüberschreitung. Bitte laden Sie die Seite neu.");
        }
      };
      poll();
    }
  }, []);

  const fetchBilling = async () => {
    setBillingLoading(true);
    try {
      const res = await api.get("/b2b/billing");
      setBilling(res.data);
    } catch { /* ignore */ }
    setBillingLoading(false);
  };

  const registerAsExpert = async () => {
    try {
      const res = await api.post("/expert/register");
      setIsExpert(true);
      toast.success(`Experten-Zugang aktiviert! Ihr Stellen-Code: ${res.data.stelle_code}`);
      fetchDashboard();
    } catch { toast.error("Experten-Zugang konnte nicht aktiviert werden"); }
  };

  const inviteClient = async () => {
    if (!inviteName.trim()) return;
    setInviting(true);
    try {
      const res = await api.post("/expert/invite", { client_email: inviteEmail, client_name: inviteName });
      toast.success(`Einladung für ${inviteName} erstellt`);
      setInviteEmail(""); setInviteName(""); setView("list");
    } catch { toast.error("Einladung fehlgeschlagen"); }
    setInviting(false);
  };

  const openDetail = async (clientId) => {
    setSelectedClient(clientId);
    setDetailLoading(true);
    setClientAkte(null);
    try {
      const [detailRes, akteRes] = await Promise.all([
        api.get(`/expert/client/${clientId}/detail`),
        api.get(`/akte/client/${clientId}`).catch(() => ({ data: { has_akte: false } })),
      ]);
      setClientDetail(detailRes.data);
      if (akteRes.data.has_akte || akteRes.data.akte_not_shared) setClientAkte(akteRes.data);
    } catch { toast.error("Zugriff verweigert"); }
    setDetailLoading(false);
  };

  const addNote = async () => {
    if (!noteText.trim() || !selectedClient) return;
    setSaving(true);
    try {
      await api.post("/expert/comment", { client_id: selectedClient, comment: noteText });
      toast.success("Notiz gespeichert");
      setNoteText("");
      openDetail(selectedClient);
    } catch { toast.error("Notiz konnte nicht gespeichert werden"); }
    setSaving(false);
  };

  const markDiscussed = async () => {
    if (!selectedClient) return;
    setSaving(true);
    try {
      await api.post("/expert/mark", { client_id: selectedClient, note: markNote, next_appointment: markDate });
      toast.success("Als besprochen markiert");
      setMarkNote(""); setMarkDate("");
      openDetail(selectedClient);
      fetchDashboard();
    } catch { toast.error("Markierung konnte nicht gespeichert werden"); }
    setSaving(false);
  };

  const copyCode = () => {
    if (dashboard?.stelle_code) {
      navigator.clipboard.writeText(dashboard.stelle_code);
      toast.success("Stellen-Code kopiert");
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  // Not yet expert — registration screen
  if (!isExpert) {
    return (
      <div className="page-transition max-w-2xl mx-auto text-center py-16" data-testid="expert-register">
        <SpurwechselLogo size="xl" className="justify-center mx-auto mb-4" />
        <h1 className="font-heading text-3xl font-bold tracking-tight mb-3">Stellen-Dashboard</h1>
        <p className="font-body text-muted-foreground mb-6 max-w-md mx-auto">
          Für MPU-Beratungsstellen. Verwalten Sie Ihre Teilnehmer und verfolgen Sie deren Vorbereitung.
        </p>
        <Button className="font-body bg-primary text-primary-foreground hover:bg-primary/90" onClick={registerAsExpert} data-testid="register-expert-btn">
          Als Beratungsstelle registrieren
        </Button>
      </div>
    );
  }

  const d = dashboard || { clients: [], counts: {}, actions: [], stelle_code: "" };

  return (
    <div className="page-transition space-y-6" data-testid="expert-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Stellen-Dashboard</h1>
          <div className="flex items-center gap-2 mt-1">
            <p className="font-body text-sm text-muted-foreground">{d.counts.total || 0} Teilnehmer</p>
            <span className="text-muted-foreground">·</span>
            <button onClick={copyCode} className="font-body text-xs text-primary hover:underline flex items-center gap-1">
              Code: {d.stelle_code} <Copy className="w-3 h-3" />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant={view === "billing" ? "secondary" : "outline"} className="font-body gap-1 text-xs" onClick={() => { setView("billing"); if (!billing) fetchBilling(); }} data-testid="billing-tab">
            <CreditCard className="w-3.5 h-3.5" /> Abrechnung
          </Button>
          <Button size="sm" className="font-body gap-1 bg-primary text-primary-foreground" onClick={() => setView(view === "invite" ? "list" : "invite")} data-testid="invite-toggle">
            <UserPlus className="w-4 h-4" /> Einladen
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Gesamt", value: d.counts.total || 0, color: "text-foreground" },
          { label: "Aktiv", value: d.counts.aktiv || 0, color: "text-warning" },
          { label: "Bereit", value: d.counts.bereit || 0, color: "text-success" },
          { label: "Inaktiv", value: d.counts.inaktiv || 0, color: "text-muted-foreground" },
        ].map((s, i) => (
          <Card key={i} className="border-border/50">
            <CardContent className="pt-4 pb-3 text-center">
              <p className={`font-heading text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className="font-body text-xs text-muted-foreground">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ═══ BILLING TAB ═══ */}
      {view === "billing" && (
        <>
          {paymentProcessing && (
            <Card className="border-primary/30 bg-primary/5" data-testid="payment-processing">
              <CardContent className="pt-4 pb-4 flex items-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                <div>
                  <p className="font-body text-sm font-medium">Zahlung wird verarbeitet...</p>
                  <p className="font-body text-xs text-muted-foreground">Bitte warten Sie einen Moment.</p>
                </div>
              </CardContent>
            </Card>
          )}
          <BillingSection billing={billing} loading={billingLoading} onRefresh={fetchBilling} />
        </>
      )}

      {/* Active Recommendations */}
      {view !== "billing" && d.actions.length > 0 && (
        <Card className="border-primary/20 bg-primary/5" data-testid="actions-card">
          <CardContent className="pt-4 pb-3">
            <p className="font-body text-xs font-medium text-primary mb-2">Empfohlene Aktionen</p>
            <div className="space-y-2">
              {d.actions.map((a, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  {a.type === "bereit" && <Check className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />}
                  {a.type === "inaktiv" && <Clock className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />}
                  {a.type === "stuck" && <AlertTriangle className="w-4 h-4 text-warning mt-0.5 flex-shrink-0" />}
                  <p className="font-body text-sm"><span className="font-medium">{a.name}</span>: {a.text}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invite Panel */}
      {view === "invite" && view !== "billing" && (
        <Card className="border-border/50" data-testid="invite-panel">
          <CardHeader><CardTitle className="font-heading text-lg">Teilnehmer einladen</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 p-3 rounded-lg">
              <p className="font-body text-xs font-medium mb-1">Einladungslink (einfachste Methode)</p>
              <div className="flex gap-2">
                <Input value={`${window.location.origin}/register?stelle=${d.stelle_code}`} readOnly className="font-body text-xs" />
                <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/register?stelle=${d.stelle_code}`); toast.success("Link kopiert"); }}>
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            <div className="border-t pt-4">
              <p className="font-body text-xs font-medium mb-3">Oder manuell einladen</p>
              <div className="space-y-3">
                <div><Label className="font-body text-sm">Name</Label><Input value={inviteName} onChange={e => setInviteName(e.target.value)} className="mt-1 font-body" data-testid="invite-name" /></div>
                <div><Label className="font-body text-sm">E-Mail (optional)</Label><Input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} className="mt-1 font-body" data-testid="invite-email" /></div>
                <Button onClick={inviteClient} disabled={inviting || !inviteName.trim()} className="font-body bg-primary text-primary-foreground gap-2" data-testid="send-invite-btn">
                  {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Einladung erstellen
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Client List */}
      {view !== "billing" && (d.clients.length > 0 ? (
        <div className="space-y-2" data-testid="client-list">
          {d.clients.map((c, i) => {
            const cfg = STATUS_CONFIG[c.status] || STATUS_CONFIG.neu;
            return (
              <Card key={i} className={`border-border/50 hover:border-primary/20 cursor-pointer transition-all ${c.status === "bereit" || c.status === "simulation_bestanden" ? "border-l-4 border-l-success" : ""}`}
                onClick={() => openDetail(c.client_id)} data-testid={`client-row-${i}`}>
                <CardContent className="py-3 px-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${cfg.color}`} />
                      <div className="min-w-0">
                        <p className="font-body text-sm font-medium truncate">{c.name}</p>
                        <p className="font-body text-xs text-muted-foreground">{c.status_label}{c.status_sub ? ` · ${c.status_sub}` : ""}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge variant="secondary" className="font-body text-[10px] hidden sm:inline-flex">{c.stage_name}</Badge>
                      {c.marked && <Badge variant="outline" className="font-body text-[10px] gap-0.5"><Check className="w-2.5 h-2.5" />Bespr.</Badge>}
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-border/50"><CardContent className="py-12 text-center">
          <Users className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="font-body text-sm text-muted-foreground mb-4">Noch keine Teilnehmer. Laden Sie Ihren ersten Teilnehmer ein.</p>
          <Button variant="outline" className="font-body gap-1" onClick={() => setView("invite")}><UserPlus className="w-4 h-4" /> Einladen</Button>
        </CardContent></Card>
      ))}

      {/* Client Detail Dialog */}
      <Dialog open={!!clientDetail} onOpenChange={() => { setClientDetail(null); setSelectedClient(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {detailLoading ? (
            <div className="py-12 text-center"><Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" /></div>
          ) : clientDetail && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <DialogTitle className="font-heading text-xl">{clientDetail.client.name}</DialogTitle>
                    <p className="font-body text-xs text-muted-foreground">
                      Stufe: {STAGE_NAMES[clientDetail.profile.current_stage] || clientDetail.profile.current_stage} · {clientDetail.profile.total_sessions} Sessions
                    </p>
                  </div>
                  {clientDetail.profile.total_sessions > 0 && (
                    <Button variant="outline" size="sm" className="font-body text-xs gap-1" onClick={() => {
                      const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/progress/${selectedClient}`;
                      window.open(url, '_blank');
                    }} data-testid="download-report-btn">
                      <FileDown className="w-3.5 h-3.5" /> Bericht
                    </Button>
                  )}
                </div>
              </DialogHeader>

              <div className="space-y-4 mt-2">
                {/* Summary */}
                <Card className={`border-l-4 ${clientDetail.status.status === "bereit" || clientDetail.status.status === "simulation_bestanden" ? "border-l-success bg-success/5" : "border-l-primary bg-primary/5"}`}>
                  <CardContent className="pt-4 pb-3">
                    <p className="font-body text-sm leading-relaxed">{clientDetail.summary}</p>
                    {clientDetail.trend && (
                      <p className="font-body text-xs text-muted-foreground mt-2 italic">{clientDetail.trend}</p>
                    )}
                    {clientDetail.focus.length > 0 && (
                      <p className="font-body text-xs text-primary font-medium mt-2">Fokus: {clientDetail.focus.join(", ")}</p>
                    )}
                    {clientDetail.action_hint && (
                      <p className="font-body text-xs font-medium mt-1">Empfehlung: {clientDetail.action_hint}</p>
                    )}
                  </CardContent>
                </Card>

                {/* Recommendation action */}
                {clientDetail.status.recommendation && (
                  <Card className="border-success/30 bg-success/5">
                    <CardContent className="pt-4 pb-3">
                      <div className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-body text-sm font-medium">{clientDetail.status.recommendation}</p>
                          {!clientDetail.mark && (
                            <div className="mt-3 space-y-2">
                              <Input placeholder="Notiz (optional)" value={markNote} onChange={e => setMarkNote(e.target.value)} className="font-body text-sm h-9" />
                              <Input type="date" placeholder="Nächster Termin" value={markDate} onChange={e => setMarkDate(e.target.value)} className="font-body text-sm h-9" />
                              <Button size="sm" onClick={markDiscussed} disabled={saving} className="font-body text-xs gap-1">
                                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Als besprochen markieren
                              </Button>
                            </div>
                          )}
                          {clientDetail.mark && (
                            <p className="font-body text-xs text-muted-foreground mt-2">
                              Besprochen am {new Date(clientDetail.mark.marked_at).toLocaleDateString("de-DE")}
                              {clientDetail.mark.note && `: ${clientDetail.mark.note}`}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Akte-Kontext */}
                {clientAkte && clientAkte.akte_not_shared && (
                  <Card className="border-border/50 bg-muted/20" data-testid="client-akte-section">
                    <CardContent className="pt-4 pb-3">
                      <p className="font-body text-xs font-medium text-muted-foreground mb-1">Akte-Kontext</p>
                      <p className="font-body text-xs text-muted-foreground">Klient hat die Akte-Freigabe nicht erteilt. Die Akte-Zusammenfassung wird erst sichtbar, wenn der Klient diese aktiv freigibt.</p>
                    </CardContent>
                  </Card>
                )}
                {clientAkte && clientAkte.has_akte && !clientAkte.akte_not_shared && (
                  <Card className="border-border/50 bg-muted/20" data-testid="client-akte-section">
                    <CardContent className="pt-4 pb-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-body text-xs font-medium text-muted-foreground">Akte-Kontext</p>
                        <Badge variant={clientAkte.risiko_level === "schwer" ? "destructive" : clientAkte.risiko_level === "mittel" ? "secondary" : "default"} className="font-body text-[10px]">
                          {clientAkte.risiko_level === "schwer" ? "Schwerer Fall" : clientAkte.risiko_level === "mittel" ? "Mittlerer Fall" : "Leichter Fall"}
                        </Badge>
                      </div>
                      <div className="space-y-1.5 font-body text-xs">
                        {clientAkte.delikt_art && <p><span className="text-muted-foreground">Delikt:</span> <span className="font-medium">{clientAkte.delikt_art}</span></p>}
                        {clientAkte.bak_wert && <p><span className="text-muted-foreground">BAK:</span> <span className="font-medium">{clientAkte.bak_wert} ‰</span></p>}
                        {clientAkte.substanz && <p><span className="text-muted-foreground">Substanz:</span> <span className="font-medium">{clientAkte.substanz}</span></p>}
                        {clientAkte.fragestellung_gutachter && (
                          <div className="bg-primary/5 rounded p-2 mt-1">
                            <p className="text-[10px] text-primary font-medium mb-0.5">Fragestellung Gutachter:</p>
                            <p className="italic text-foreground">"{clientAkte.fragestellung_gutachter}"</p>
                          </div>
                        )}
                        {clientAkte.auflagen?.length > 0 && <p><span className="text-muted-foreground">Auflagen:</span> {clientAkte.auflagen.join(", ")}</p>}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Stage Progress */}
                <div>
                  <p className="font-body text-xs font-medium text-muted-foreground mb-2">Stufenverlauf</p>
                  <div className="flex gap-1.5 flex-wrap">
                    {["ankommen", "verstehen", "aufarbeiten", "trainieren", "vertiefen", "simulation"].map(s => {
                      const completed = clientDetail.profile.stages_completed.includes(s);
                      const current = clientDetail.profile.current_stage === s;
                      return (
                        <Badge key={s} variant={completed ? "default" : current ? "secondary" : "outline"}
                          className={`font-body text-[10px] ${completed ? "bg-success text-white" : current ? "" : "opacity-50"}`}>
                          {completed && <Check className="w-2.5 h-2.5 mr-0.5" />}
                          {STAGE_NAMES[s]}
                        </Badge>
                      );
                    })}
                  </div>
                </div>

                {/* Competencies */}
                {Object.keys(clientDetail.profile.competencies).length > 0 && (
                  <div>
                    <p className="font-body text-xs font-medium text-muted-foreground mb-2">Kompetenzen</p>
                    <div className="space-y-2">
                      {Object.entries(clientDetail.profile.competencies).map(([k, v]) => (
                        <div key={k}>
                          <div className="flex justify-between font-body text-xs mb-0.5">
                            <span>{COMP_LABELS[k] || k}</span>
                            <span className="font-medium">{v}/10</span>
                          </div>
                          <Progress value={v * 10} className="h-1.5" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Feedback Excerpts */}
                {clientDetail.feedback_excerpts.length > 0 && (
                  <div>
                    <p className="font-body text-xs font-medium text-muted-foreground mb-2">Letzte Beobachtungen</p>
                    <div className="space-y-2">
                      {clientDetail.feedback_excerpts.map((f, i) => (
                        <div key={i} className="flex gap-2 text-xs">
                          <span className="font-body text-muted-foreground flex-shrink-0 w-16">{f.date?.slice(5)}</span>
                          <Badge variant="secondary" className="text-[9px] flex-shrink-0">{f.stage}</Badge>
                          <span className="font-body text-muted-foreground">{f.text.slice(0, 100)}...</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Berater Notes */}
                <div>
                  <p className="font-body text-xs font-medium text-muted-foreground mb-2">Ihre Notizen</p>
                  <div className="flex gap-2 mb-3">
                    <Textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Notiz hinzufügen..." className="font-body text-sm min-h-[60px]" />
                    <Button size="sm" onClick={addNote} disabled={saving || !noteText.trim()} className="self-end"><Send className="w-3.5 h-3.5" /></Button>
                  </div>
                  {clientDetail.comments.map((c, i) => (
                    <div key={i} className="p-2.5 bg-muted/50 rounded-lg mb-2">
                      <p className="font-body text-sm">{c.comment}</p>
                      <p className="font-body text-[10px] text-muted-foreground mt-1">{new Date(c.created_at).toLocaleDateString("de-DE")} · {c.expert_name}</p>
                    </div>
                  ))}
                </div>

                {/* Gesprächs-Protokoll */}
                {clientDetail.mark_history?.length > 0 && (
                  <div>
                    <p className="font-body text-xs font-medium text-muted-foreground mb-2">Gesprächs-Protokoll</p>
                    {clientDetail.mark_history.map((m, i) => (
                      <div key={i} className="flex gap-2 p-2 bg-success/5 rounded-lg mb-1.5 text-xs">
                        <Check className="w-3 h-3 text-success mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-body font-medium">{new Date(m.marked_at).toLocaleDateString("de-DE")}</span>
                          {m.note && <span className="font-body text-muted-foreground"> — {m.note}</span>}
                          {m.next_appointment && <span className="font-body text-primary"> · Nächster Termin: {m.next_appointment}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
